import { useEffect, useState } from 'react';
import { useField } from 'formik';
import { KoobInputSelect } from '@koob/ui';

const PREVIEW_LIMIT = 40;

// Batches the currently visible rows into one Google Fonts CSS2 request
// (weight 400 only — this is just enough to preview each row's shape, the
// resolved theme's real weights are handled separately by branding.js) so
// the list renders every row in its own font instead of the UI font. Capped
// to PREVIEW_LIMIT: batching all ~1500 catalogue fonts in one request just
// to open the picker isn't worth it — the list is scrollable, only what's
// visible (plus a little headroom) needs to be real.
function buildPreviewFontsUrl(families) {
  if (families.length === 0) return null;
  const familyParams = families
    .map(
      (family) => `family=${encodeURIComponent(family).replace(/%20/g, '+')}`,
    )
    .join('&');
  return `https://fonts.googleapis.com/css2?${familyParams}&display=swap`;
}

function useBatchedFontFaces(families) {
  useEffect(() => {
    if (families.length === 0) return undefined;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = buildPreviewFontsUrl(families);
    document.head.appendChild(link);
    return () => document.head.removeChild(link);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [families.join('|')]);
}

export default function FontPickerField({
  label,
  name,
  fonts,
  isLoadingFonts,
  sampleText,
  placeholder = 'Choose a font',
}) {
  const [field, , helpers] = useField(name);
  const [query, setQuery] = useState('');

  const filteredFonts = query
    ? fonts.filter((font) =>
        font.family.toLowerCase().includes(query.toLowerCase()),
      )
    : fonts;
  const options = filteredFonts
    .slice(0, 200)
    .map((font) => ({ value: font.family, label: font.family }));

  useBatchedFontFaces(options.slice(0, PREVIEW_LIMIT).map((o) => o.value));

  return (
    <div>
      <KoobInputSelect
        label={label}
        options={options}
        value={field.value}
        onValueChange={helpers.setValue}
        placeholder={placeholder}
        searchable
        onSearchChange={setQuery}
        loading={isLoadingFonts}
        emptyState={
          isLoadingFonts ? 'Loading fonts…' : 'No font matches your search'
        }
        renderValue={(option) => (
          <span style={{ fontFamily: `"${option.value}"` }}>
            {option.label}
          </span>
        )}
        renderOption={(option) => (
          <span className="flex w-full min-w-0 items-center justify-between gap-2">
            <span
              className="min-w-0 flex-1 truncate"
              style={{ fontFamily: `"${option.value}"` }}
            >
              {option.label}
            </span>
            <span
              className="shrink-0 text-base text-gray-400"
              style={{ fontFamily: `"${option.value}"` }}
            >
              Aa
            </span>
          </span>
        )}
      />
      {sampleText && field.value && (
        <div
          className="mt-2 truncate text-sm text-slate-500"
          style={{ fontFamily: `"${field.value}"` }}
        >
          {sampleText}
        </div>
      )}
    </div>
  );
}
